import React from 'react';
import intl from "react-intl-universal";
import classNames from "classnames";
import {observer} from "mobx-react-lite";
import {useAppState, useRootStore, useDsManager} from "@stores/AppStateContext";

import {ContextMenuProps} from "../../elements/context-menu";

import {Select, SelectOption} from "../form/Select";
import {selectWorkspaceFromPlain} from "../../../actions/ui/selectWorkspace";
import {AddIcon} from "../icons/AddIcon";
import {CheckIcon} from "../icons/CheckIcon";
import {WorkspaceAvatar} from "../Avatar/Avatar";
import {workspaceName} from "../../../utils/workspaceName";

import {MODALS} from "../../screens/modals/modal-names";

import "./WorkspaceSelect.css"

export const WorkspaceSelect = observer(() => {
    const appState = useAppState();
    const rootStore = useRootStore();
    const dsManager = useDsManager();
    const state = appState.userState;
    if (!state) {
        return null;
    }

    const availableWorkspaces = state.availableWorkspaces;
    const getSelectedWorkspace = (selectedValue: string | null) => {
        return availableWorkspaces.find(workspace => workspace.id === selectedValue) || null;
    };

    const handleWorkspaceChange = (selectedValue: string | null) => {
        const selectedWorkspace = availableWorkspaces.find(workspace => workspace.id === selectedValue);
        if (selectedWorkspace) {
            selectWorkspaceFromPlain(rootStore, state.dsManager!, selectedWorkspace);
        }
    };

    const workspaceOptions: SelectOption[] = availableWorkspaces.map(workspace => ({
        value: workspace.id,
        label: (
            <div className="WorkspaceSelect__Selected">
                <WorkspaceAvatar
                    sx={{borderRadius: '6px'}}
                    workspace={workspace}
                    size={24}
                    className={'WorkspaceSelect__Avatar--small'}
                />
                <div className={classNames({
                    "WorkspaceRow__RightBlockOption": true,
                    "WorkspaceSelect__RightBlockOption": rootStore.view.workspace?.id === workspace.id,
                })}>
                    <div className="WorkspaceSelect__name body-large">
                        {workspaceName(workspace)}
                    </div>
                </div>
            </div>
        ),
        activeIcon: rootStore.view.workspace?.id === workspace.id ? CheckIcon : undefined,
        moreAction: (e: React.MouseEvent<HTMLDivElement>) => {
            e.stopPropagation();
            const target = (e.target as HTMLDivElement);
            if (appState.additionalContextMenus.find((ctxMenu: ContextMenuProps) => ctxMenu.id === workspace.id)) {
                appState.removeAdditionalContextMenu()
            } else {
                appState.addAdditionalContextMenu({
                    id: workspace.id,
                    type: 'workspace',
                    shouldForcePlace: true,
                    menuPlacement: 'block-end',
                    menuAlignment: 'center',
                    isDark: true,
                    VERTICAL_SHIFT: 0,
                    HORIZONTAL_SHIFT: 0,
                    getTargetBoundingBox: target.getBoundingClientRect.bind(target),
                    params: {workspace: workspace}
                }, false);
            }
        }
    }));

    return (
        <Select
            className="WorkspaceSelect"
            id="WorkspaceSelect"
            maxHeight={408}
            maxWidth={300}
            iconSize={20}
            isDark={true}
            hasIconBeforeHeader={true}
            ignoreActiveHighlight={true}
            headerNode={
                <div className="label-small-bold WorkspaceSelect_header">
                    {intl.get('workspace.common').d('WORKSPACES')}
                    <AddIcon size={20} className="Workspace__AddIcon"
                             onClick={() => {
                                 appState.setModal({name: MODALS.CREATE_WORKSPACE, params: undefined});
                                 appState.setContextMenu(null);
                             }}/>
                </div>}
            isFullWidth={true}
            size="large"
            selectedValue={rootStore.view.workspace?.id || null}
            shouldForcePlace={true}
            options={workspaceOptions}
            renderSelectedOption={(selectedOption) => {
                const selectedWorkspace = getSelectedWorkspace(selectedOption.value)!;

                return (
                    <div className="WorkspaceSelect__Selected">
                        <WorkspaceAvatar
                            sx={{borderRadius: '6px'}}
                            workspace={selectedWorkspace}
                            size={40}
                            className={'WorkspaceSelect__Avatar--medium'}
                        />
                        <div className="WorkspaceSelect__RightBlock">
                            <div className={classNames({
                                "WorkspaceSelect__name title-medium": true,
                                "WorkspaceSelect__name--active": appState.contextMenu?.id === 'WorkspaceSelect'
                            })}>
                                {workspaceName(selectedWorkspace)}
                            </div>
                            <div className="WorkspaceSelect__CompanyName body-small">
                                {dsManager!.clientAR.row?.name}
                            </div>
                        </div>
                    </div>
                );
            }}
            onChange={handleWorkspaceChange}
        />
    );
});
